import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart3, ArrowLeft, TrendingUp, Users, Shield, Home, Calendar, Download, RefreshCw, Clock, FileText, AlertTriangle, CheckCircle, Building2, Eye, ArrowRight, Mail, User, Building, Lock, LogOut } from "lucide-react";
import "@/styles/govtech.css";

// Check if user has already registered to view dashboard
const getDashboardAccess = () => {
  try {
    const access = localStorage.getItem('tenures_dashboard_access');
    return access ? JSON.parse(access) : null;
  } catch {
    return null;
  }
};

const saveDashboardAccess = (accessData) => {
  localStorage.setItem('tenures_dashboard_access', JSON.stringify(accessData));
};

// Simulated analytics data (in production, this would aggregate from localStorage or a backend)
const getAnalyticsData = () => {
  const storedData = localStorage.getItem('tenures_analytics');
  if (storedData) {
    return JSON.parse(storedData);
  }
  
  // Generate realistic demo data
  const today = new Date();
  const demoData = {
    lastUpdated: today.toISOString(),
    period: "Last 30 Days",
    
    // Module Usage
    moduleUsage: {
      succession: { total: 47, completed: 38, pdfDownloads: 31 },
      rtb: { total: 62, completed: 55, pdfDownloads: 42 },
      liveability: { total: 28, completed: 24, pdfDownloads: 18 },
      milestone: { total: 35, completed: 31, pdfDownloads: 27 },
      exchange: { total: 19, listingsCreated: 15, matchesFound: 7 },
      arrears: { total: 23, lettersGenerated: 18 }
    },
    
    // Succession Outcomes
    successionOutcomes: {
      green: 14,
      amber: 18,
      red: 6
    },
    
    // RTB Interest
    rtbInterest: {
      highIntent: 22,
      exploring: 25,
      notEligible: 8
    },
    
    // Under-Occupation
    underOccupation: {
      interestedInDownsizing: 12,
      potentialSavings: 8400, // Monthly across all interested
      bedroomsToFree: 15
    },
    
    // Arrears Prevention Insights
    arrearsInsights: {
      earlyInterventions: 23,
      residentsAtRisk: 31,
      supportReferrals: 18,
      avgEvictionCost: 12000, // Average cost per eviction (legal + void + rehousing)
      preventionRate: 78 // Percentage of at-risk residents who engaged with support
    },
    
    // Engagement Trends (last 7 days)
    dailyEngagement: [
      { day: "Mon", visits: 18 },
      { day: "Tue", visits: 24 },
      { day: "Wed", visits: 31 },
      { day: "Thu", visits: 27 },
      { day: "Fri", visits: 22 },
      { day: "Sat", visits: 12 },
      { day: "Sun", visits: 9 }
    ]
  };
  
  localStorage.setItem('tenures_analytics', JSON.stringify(demoData));
  return demoData;
};

// Components
const TenuresLogo = () => (
  <span className="text-xl font-bold text-[#333333]">
    Ten<span className="text-[#00703C] font-extrabold">UR</span>es<span className="text-[#00703C]">.co.uk</span>
  </span>
);

const StatCard = ({ icon: Icon, label, value, subtext, trend, color = "#00703C" }) => (
  <div className="bg-white border border-gray-200 p-5">
    <div className="flex items-start justify-between mb-3">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon style={{ color }} size={20} />
      </div>
      {trend && (
        <span className={`text-xs px-2 py-1 rounded ${trend > 0 ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
          {trend > 0 ? '+' : ''}{trend}%
        </span>
      )}
    </div>
    <p className="text-2xl font-bold text-[#333333]">{value}</p>
    <p className="text-sm text-gray-600">{label}</p>
    {subtext && <p className="text-xs text-gray-400 mt-1">{subtext}</p>}
  </div>
);

const ModuleCard = ({ icon: Icon, title, stats, color = "#00703C" }) => (
  <div className="bg-white border border-gray-200 p-5">
    <div className="flex items-center gap-3 mb-4">
      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
        <Icon style={{ color }} size={20} />
      </div>
      <h3 className="font-bold text-[#333333]">{title}</h3>
    </div>
    <div className="space-y-2">
      {stats.map((stat, i) => (
        <div key={i} className="flex justify-between items-center text-sm">
          <span className="text-gray-600">{stat.label}</span>
          <span className="font-semibold text-[#333333]">{stat.value}</span>
        </div>
      ))}
    </div>
  </div>
);

const OutcomeBar = ({ label, value, total, color }) => {
  const percentage = total > 0 ? (value / total) * 100 : 0;
  return (
    <div className="mb-3">
      <div className="flex justify-between items-center mb-1">
        <span className="text-sm text-gray-600">{label}</span>
        <span className="text-sm font-semibold">{value} ({Math.round(percentage)}%)</span>
      </div>
      <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
        <div 
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${percentage}%`, backgroundColor: color }}
        />
      </div>
    </div>
  );
};

const SimpleBarChart = ({ data }) => {
  const maxValue = Math.max(...data.map(d => d.visits));
  return (
    <div className="flex items-end justify-between gap-2 h-32">
      {data.map((item, i) => (
        <div key={i} className="flex-1 flex flex-col items-center">
          <div 
            className="w-full bg-[#00703C] rounded-t transition-all duration-300 hover:bg-[#005a30]"
            style={{ height: `${(item.visits / maxValue) * 100}%`, minHeight: '4px' }}
          />
          <span className="text-xs text-gray-500 mt-2">{item.day}</span>
        </div>
      ))}
    </div>
  );
};

export default function B2BDashboard() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [userData, setUserData] = useState(null);
  const [accessForm, setAccessForm] = useState({
    name: "",
    email: "",
    organization: "",
    role: ""
  });
  const [formError, setFormError] = useState("");
  
  useEffect(() => {
    // Check if user already has access
    const existingAccess = getDashboardAccess();
    if (existingAccess) {
      setHasAccess(true);
      setUserData(existingAccess);
    }
  }, []);
  
  useEffect(() => {
    if (hasAccess) {
      // Simulate loading
      setTimeout(() => {
        setData(getAnalyticsData());
        setLoading(false);
      }, 800);
    }
  }, [hasAccess]);
  
  const handleAccessSubmit = (e) => {
    e.preventDefault();
    setFormError("");
    
    // Validate form
    if (!accessForm.name.trim()) {
      setFormError("Please enter your name");
      return;
    }
    if (!accessForm.email.trim() || !accessForm.email.includes("@")) {
      setFormError("Please enter a valid email address");
      return;
    }
    if (!accessForm.organization.trim()) {
      setFormError("Please enter your organization");
      return;
    }
    
    // Save access and log lead
    const accessData = {
      ...accessForm,
      grantedAt: new Date().toISOString()
    };
    saveDashboardAccess(accessData);
    console.log("[Dashboard Lead Captured]", accessData);
    setUserData(accessData);
    setHasAccess(true);
    setLoading(true);
  };
  
  const handleSignOut = () => {
    localStorage.removeItem('tenures_dashboard_access');
    setHasAccess(false);
    setUserData(null);
    setLoading(false);
    setData(null);
  };
  
  const handleRefresh = () => {
    setLoading(true);
    setTimeout(() => {
      // Regenerate with slight variations
      const newData = getAnalyticsData();
      newData.lastUpdated = new Date().toISOString();
      setData(newData);
      setLoading(false);
    }, 500);
  };
  
  const handleExportCSV = () => {
    if (!data) return;
    
    const csvContent = `TenURes Authority Dashboard Export
Generated: ${new Date().toLocaleDateString('en-GB')}
Period: ${data.period}

MODULE USAGE
Module,Sessions,Completions,PDF Downloads
Succession Rights,${data.moduleUsage.succession.total},${data.moduleUsage.succession.completed},${data.moduleUsage.succession.pdfDownloads}
Right to Buy,${data.moduleUsage.rtb.total},${data.moduleUsage.rtb.completed},${data.moduleUsage.rtb.pdfDownloads}
Liveability,${data.moduleUsage.liveability.total},${data.moduleUsage.liveability.completed},${data.moduleUsage.liveability.pdfDownloads}
Milestone Guardian,${data.moduleUsage.milestone.total},${data.moduleUsage.milestone.completed},${data.moduleUsage.milestone.pdfDownloads}
Exchange Matcher,${data.moduleUsage.exchange.total},${data.moduleUsage.exchange.listingsCreated},${data.moduleUsage.exchange.matchesFound}
Arrears Support,${data.moduleUsage.arrears.total},${data.moduleUsage.arrears.lettersGenerated},-

SUCCESSION OUTCOMES
Status,Count
High Probability (Green),${data.successionOutcomes.green}
Requires Evidence (Amber),${data.successionOutcomes.amber}
Criteria Not Met (Red),${data.successionOutcomes.red}

RTB INTEREST
Category,Count
High Intent,${data.rtbInterest.highIntent}
Exploring,${data.rtbInterest.exploring}
Not Eligible,${data.rtbInterest.notEligible}

UNDER-OCCUPATION
Metric,Value
Interested in Downsizing,${data.underOccupation.interestedInDownsizing}
Potential Monthly Savings,£${data.underOccupation.potentialSavings}
Bedrooms to Free,${data.underOccupation.bedroomsToFree}
`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `TenURes_Dashboard_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };
  
  // Access Gate - Show registration form if user hasn't registered
  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        {/* Navigation */}
        <nav className="border-b border-gray-200 bg-white">
          <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
            <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
              <ArrowLeft size={20} />
              <span>Back to Home</span>
            </button>
            <TenuresLogo />
          </div>
        </nav>
        
        {/* Access Gate */}
        <div className="max-w-5xl mx-auto px-4 py-12">
          <div className="grid md:grid-cols-2 gap-8 items-center">
            {/* Left - Info */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1 bg-[#333333] text-white text-sm font-medium mb-4">
                <Building size={14} />
                For Housing Professionals
              </div>
              <h1 className="text-3xl md:text-4xl font-bold text-[#333333] mb-4">
                See What Your Residents Are Asking
              </h1>
              <p className="text-gray-600 mb-6">
                Preview our authority dashboard with sample data. See how TenURes helps you understand resident needs before they become enquiries.
              </p>
              
              <div className="space-y-4 mb-8">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="text-[#00703C]" size={16} />
                  </div>
                  <div>
                    <p className="font-medium text-[#333333]">Pre-Qualified Succession Claims</p>
                    <p className="text-sm text-gray-500">See who's likely eligible before they call</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="text-[#00703C]" size={16} />
                  </div>
                  <div>
                    <p className="font-medium text-[#333333]">RTB Pipeline Visibility</p>
                    <p className="text-sm text-gray-500">Forecast capital receipts from Right to Buy interest</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#00703C] bg-opacity-10 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <CheckCircle className="text-[#00703C]" size={16} />
                  </div>
                  <div>
                    <p className="font-medium text-[#333333]">Downsizing Opportunities</p>
                    <p className="text-sm text-gray-500">Identify under-occupied homes ready for exchange</p>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-amber-50 border border-amber-200">
                <p className="text-sm text-amber-800">
                  <strong>Sample Data:</strong> This is a demo with representative figures. Your authority's dashboard will show real engagement from your residents.
                </p>
              </div>
            </div>
            
            {/* Right - Form */}
            <div className="bg-white border border-gray-200 p-8">
              <h2 className="text-xl font-bold text-[#333333] mb-2">Access Demo Dashboard</h2>
              <p className="text-sm text-gray-500 mb-6">Quick registration for instant access</p>
              
              <form onSubmit={handleAccessSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">
                    <User size={14} className="inline mr-1" />
                    Your Name *
                  </label>
                  <input
                    type="text"
                    className="govtech-input"
                    placeholder="Jane Smith"
                    value={accessForm.name}
                    onChange={(e) => setAccessForm({...accessForm, name: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">
                    <Mail size={14} className="inline mr-1" />
                    Work Email *
                  </label>
                  <input
                    type="email"
                    className="govtech-input"
                    placeholder="jane.smith@council.gov.uk"
                    value={accessForm.email}
                    onChange={(e) => setAccessForm({...accessForm, email: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">
                    <Building size={14} className="inline mr-1" />
                    Organization *
                  </label>
                  <input
                    type="text"
                    className="govtech-input"
                    placeholder="Birmingham City Council"
                    value={accessForm.organization}
                    onChange={(e) => setAccessForm({...accessForm, organization: e.target.value})}
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-[#333333] mb-1">
                    Job Title (Optional)
                  </label>
                  <input
                    type="text"
                    className="govtech-input"
                    placeholder="Housing Director"
                    value={accessForm.role}
                    onChange={(e) => setAccessForm({...accessForm, role: e.target.value})}
                  />
                </div>
                
                {formError && (
                  <div className="p-3 bg-red-50 border border-red-200 text-red-700 text-sm">
                    {formError}
                  </div>
                )}
                
                <button
                  type="submit"
                  className="govtech-btn govtech-btn-primary w-full"
                >
                  Access Demo Dashboard
                  <ArrowRight size={18} className="ml-2" />
                </button>
                
                <p className="text-xs text-gray-400 text-center">
                  We'll follow up to discuss how TenURes can help your authority. No spam, ever.
                </p>
              </form>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <footer className="border-t border-gray-200 py-8 bg-white mt-12">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <TenuresLogo />
            <p className="text-sm text-gray-500 mt-4">
              Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk | Privacy-First Housing Solutions
            </p>
          </div>
        </footer>
      </div>
    );
  }
  
  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-[#00703C] border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }
  
  const totalSuccession = data.successionOutcomes.green + data.successionOutcomes.amber + data.successionOutcomes.red;
  const totalSessions = Object.values(data.moduleUsage).reduce((sum, m) => sum + (m.total || 0), 0);
  const totalPDFs = (data.moduleUsage.succession.pdfDownloads || 0) + 
                   (data.moduleUsage.rtb.pdfDownloads || 0) + 
                   (data.moduleUsage.liveability.pdfDownloads || 0) + 
                   (data.moduleUsage.milestone.pdfDownloads || 0);

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Navigation */}
      <nav className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-4 py-4 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
            <ArrowLeft size={20} />
            <span>Back to Home</span>
          </button>
          <TenuresLogo />
        </div>
      </nav>
      
      {/* Header */}
      <header className="bg-[#333333] text-white py-8">
        <div className="max-w-6xl mx-auto px-4">
          {/* Personalised Greeting */}
          {userData?.name && (
            <div className="mb-4 pb-4 border-b border-gray-600">
              <p className="text-lg">
                Welcome back, <span className="font-semibold text-[#00703C]">{userData.name.split(' ')[0]}</span>
                {userData.organization && <span className="text-gray-400 text-sm ml-2">({userData.organization})</span>}
              </p>
            </div>
          )}
          
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Building2 size={24} />
                <span className="text-sm text-gray-400">Authority Dashboard</span>
                <span className="px-2 py-0.5 bg-amber-500 text-amber-900 text-xs font-bold rounded">DEMO DATA</span>
              </div>
              <h1 className="text-2xl md:text-3xl font-bold">Resident Engagement Analytics</h1>
              <p className="text-gray-400 mt-1">Anonymised, aggregated insights from your residents</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={handleRefresh}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors text-sm"
              >
                <RefreshCw size={16} />
                Refresh
              </button>
              <button
                onClick={handleExportCSV}
                className="flex items-center gap-2 px-4 py-2 bg-[#00703C] hover:bg-[#005a30] transition-colors text-sm"
              >
                <Download size={16} />
                Export CSV
              </button>
              <button
                onClick={handleSignOut}
                className="flex items-center gap-2 px-4 py-2 bg-white bg-opacity-10 hover:bg-opacity-20 transition-colors text-sm"
                title="Sign out and view as new visitor"
              >
                <LogOut size={16} />
                Sign Out
              </button>
            </div>
          </div>
          
          <div className="flex items-center gap-4 mt-6 text-sm text-gray-400">
            <div className="flex items-center gap-2">
              <Clock size={14} />
              <span>Last updated: {new Date(data.lastUpdated).toLocaleString('en-GB')}</span>
            </div>
            <div className="flex items-center gap-2">
              <Eye size={14} />
              <span>{data.period}</span>
            </div>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-4 py-8">
        {/* Key Metrics */}
        <section className="mb-8">
          <h2 className="text-lg font-bold text-[#333333] mb-4">Key Metrics</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <StatCard
              icon={Users}
              label="Total Sessions"
              value={totalSessions}
              subtext="Unique triage sessions"
              trend={12}
            />
            <StatCard
              icon={FileText}
              label="PDFs Generated"
              value={totalPDFs}
              subtext="Evidence packs downloaded"
              trend={8}
            />
            <StatCard
              icon={Home}
              label="Bedrooms to Free"
              value={data.underOccupation.bedroomsToFree}
              subtext="Via downsizing interest"
              color="#F47738"
            />
            <StatCard
              icon={TrendingUp}
              label="Potential Savings"
              value={`£${(data.underOccupation.potentialSavings).toLocaleString()}`}
              subtext="Monthly from downsizing"
              color="#00703C"
            />
          </div>
        </section>
        
        {/* Module Performance */}
        <section className="mb-8">
          <h2 className="text-lg font-bold text-[#333333] mb-4">Module Performance</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <ModuleCard
              icon={Shield}
              title="Succession Rights"
              stats={[
                { label: "Sessions Started", value: data.moduleUsage.succession.total },
                { label: "Completed", value: data.moduleUsage.succession.completed },
                { label: "PDFs Downloaded", value: data.moduleUsage.succession.pdfDownloads }
              ]}
            />
            <ModuleCard
              icon={TrendingUp}
              title="Right to Buy"
              stats={[
                { label: "Sessions Started", value: data.moduleUsage.rtb.total },
                { label: "Completed", value: data.moduleUsage.rtb.completed },
                { label: "PDFs Downloaded", value: data.moduleUsage.rtb.pdfDownloads }
              ]}
            />
            <ModuleCard
              icon={Home}
              title="Liveability / Exchange"
              stats={[
                { label: "Sessions Started", value: data.moduleUsage.liveability.total },
                { label: "Completed", value: data.moduleUsage.liveability.completed },
                { label: "PDFs Downloaded", value: data.moduleUsage.liveability.pdfDownloads }
              ]}
            />
            <ModuleCard
              icon={Calendar}
              title="Milestone Guardian"
              stats={[
                { label: "Sessions Started", value: data.moduleUsage.milestone.total },
                { label: "Completed", value: data.moduleUsage.milestone.completed },
                { label: "Reminders Set", value: data.moduleUsage.milestone.pdfDownloads }
              ]}
            />
            <ModuleCard
              icon={Users}
              title="Exchange Matcher"
              color="#F47738"
              stats={[
                { label: "Sessions Started", value: data.moduleUsage.exchange.total },
                { label: "Listings Created", value: data.moduleUsage.exchange.listingsCreated },
                { label: "Matches Found", value: data.moduleUsage.exchange.matchesFound }
              ]}
            />
            <ModuleCard
              icon={AlertTriangle}
              title="Arrears Support"
              color="#D4351C"
              stats={[
                { label: "Sessions Started", value: data.moduleUsage.arrears.total },
                { label: "Letters Generated", value: data.moduleUsage.arrears.lettersGenerated },
                { label: "Support Links Clicked", value: Math.floor(data.moduleUsage.arrears.total * 0.7) }
              ]}
            />
          </div>
        </section>
        
        {/* Two Column: Outcomes & Engagement */}
        <div className="grid md:grid-cols-2 gap-6 mb-8">
          {/* Succession Outcomes */}
          <div className="bg-white border border-gray-200 p-6">
            <h3 className="font-bold text-[#333333] mb-4">Succession Outcomes Distribution</h3>
            <OutcomeBar 
              label="High Probability (Green)" 
              value={data.successionOutcomes.green} 
              total={totalSuccession}
              color="#00703C"
            />
            <OutcomeBar 
              label="Requires Evidence (Amber)" 
              value={data.successionOutcomes.amber} 
              total={totalSuccession}
              color="#F47738"
            />
            <OutcomeBar 
              label="Criteria Not Met (Red)" 
              value={data.successionOutcomes.red} 
              total={totalSuccession}
              color="#D4351C"
            />
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                <CheckCircle className="inline text-[#00703C] mr-1" size={14} />
                {Math.round((data.successionOutcomes.green / totalSuccession) * 100)}% of succession queries show high eligibility
              </p>
            </div>
          </div>
          
          {/* Weekly Engagement */}
          <div className="bg-white border border-gray-200 p-6">
            <h3 className="font-bold text-[#333333] mb-4">Weekly Engagement Trend</h3>
            <SimpleBarChart data={data.dailyEngagement} />
            <div className="mt-4 pt-4 border-t border-gray-100">
              <p className="text-sm text-gray-600">
                Peak engagement on <strong>Wednesday</strong> with {Math.max(...data.dailyEngagement.map(d => d.visits))} sessions
              </p>
            </div>
          </div>
        </div>
        
        {/* RTB Interest Breakdown */}
        <section className="mb-8">
          <div className="bg-white border border-gray-200 p-6">
            <h3 className="font-bold text-[#333333] mb-4">Right to Buy Intent Analysis</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div className="text-center p-4 bg-[#00703C] bg-opacity-5 border border-[#00703C] border-opacity-20">
                <p className="text-3xl font-bold text-[#00703C]">{data.rtbInterest.highIntent}</p>
                <p className="text-sm text-gray-600 mt-1">High Intent</p>
                <p className="text-xs text-gray-500">Ready to proceed</p>
              </div>
              <div className="text-center p-4 bg-[#F47738] bg-opacity-5 border border-[#F47738] border-opacity-20">
                <p className="text-3xl font-bold text-[#F47738]">{data.rtbInterest.exploring}</p>
                <p className="text-sm text-gray-600 mt-1">Exploring</p>
                <p className="text-xs text-gray-500">Gathering information</p>
              </div>
              <div className="text-center p-4 bg-gray-50 border border-gray-200">
                <p className="text-3xl font-bold text-gray-500">{data.rtbInterest.notEligible}</p>
                <p className="text-sm text-gray-600 mt-1">Not Eligible</p>
                <p className="text-xs text-gray-500">Didn't meet criteria</p>
              </div>
            </div>
            <div className="mt-6 p-4 bg-gray-50">
              <h4 className="font-semibold text-[#333333] text-sm mb-2">Forecasted Capital Receipts</h4>
              <p className="text-sm text-gray-600">
                Based on {data.rtbInterest.highIntent} high-intent applications at average property value of £250,000:
              </p>
              <p className="text-2xl font-bold text-[#00703C] mt-2">
                £{((data.rtbInterest.highIntent * 250000) * 0.6).toLocaleString()} - £{((data.rtbInterest.highIntent * 250000) * 0.7).toLocaleString()}
              </p>
              <p className="text-xs text-gray-500 mt-1">Estimated range (accounting for 30-40% discount)</p>
            </div>
          </div>
        </section>
        
        {/* Stock Optimisation Opportunity */}
        <section className="mb-8">
          <div className="bg-[#00703C] text-white p-6">
            <h3 className="font-bold text-xl mb-4">Stock Optimisation Opportunity</h3>
            <div className="grid md:grid-cols-3 gap-6">
              <div>
                <p className="text-4xl font-bold">{data.underOccupation.interestedInDownsizing}</p>
                <p className="text-green-200">Residents interested in downsizing</p>
              </div>
              <div>
                <p className="text-4xl font-bold">{data.underOccupation.bedroomsToFree}</p>
                <p className="text-green-200">Bedrooms potentially freed</p>
              </div>
              <div>
                <p className="text-4xl font-bold">£{data.underOccupation.potentialSavings.toLocaleString()}</p>
                <p className="text-green-200">Monthly savings for residents</p>
              </div>
            </div>
            <div className="mt-6 pt-4 border-t border-green-600">
              <p className="text-sm text-green-200">
                These residents have completed the Liveability Triage and expressed interest in mutual exchange or downsizing incentives.
              </p>
            </div>
          </div>
        </section>
        
        {/* Arrears Prevention Insights */}
        <section className="mb-8">
          <div className="bg-gradient-to-r from-[#D4351C] to-[#B02A15] text-white p-6">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle size={24} />
              <h3 className="font-bold text-xl">Arrears Prevention Insights</h3>
              <span className="px-2 py-0.5 bg-white bg-opacity-20 text-xs font-semibold rounded">EARLY WARNING</span>
            </div>
            
            <div className="grid md:grid-cols-4 gap-6 mb-6">
              <div>
                <p className="text-4xl font-bold">{data.arrearsInsights?.earlyInterventions || 23}</p>
                <p className="text-red-200">Early interventions identified</p>
              </div>
              <div>
                <p className="text-4xl font-bold">{data.arrearsInsights?.residentsAtRisk || 31}</p>
                <p className="text-red-200">Residents seeking support</p>
              </div>
              <div>
                <p className="text-4xl font-bold">{data.arrearsInsights?.supportReferrals || 18}</p>
                <p className="text-red-200">Referred to support services</p>
              </div>
              <div>
                <p className="text-4xl font-bold">{data.arrearsInsights?.preventionRate || 78}%</p>
                <p className="text-red-200">Engaged with early support</p>
              </div>
            </div>
            
            {/* Cost Savings Highlight */}
            <div className="bg-white bg-opacity-10 p-4 rounded">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <p className="text-sm text-red-200 mb-1">Estimated Savings from Early Intervention</p>
                  <p className="text-3xl md:text-4xl font-bold">
                    £{((data.arrearsInsights?.earlyInterventions || 23) * (data.arrearsInsights?.avgEvictionCost || 12000)).toLocaleString()}
                  </p>
                  <p className="text-xs text-red-200 mt-1">
                    Based on {data.arrearsInsights?.earlyInterventions || 23} potential evictions avoided × £{(data.arrearsInsights?.avgEvictionCost || 12000).toLocaleString()} avg. eviction cost
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm text-red-200">Average eviction cost includes:</p>
                  <p className="text-xs text-red-300">Legal fees • Void period • Court costs • Rehousing</p>
                </div>
              </div>
            </div>
            
            <div className="mt-4 pt-4 border-t border-red-400">
              <p className="text-sm text-red-200">
                <CheckCircle className="inline mr-2" size={16} />
                Residents who access arrears support early are <strong>3x more likely</strong> to maintain their tenancy than those who don't seek help.
              </p>
            </div>
          </div>
        </section>
        
        {/* Privacy Notice */}
        <div className="bg-gray-50 border border-gray-200 p-4 text-center">
          <p className="text-sm text-gray-600">
            <Shield className="inline mr-2 text-[#00703C]" size={16} />
            All data is anonymised and aggregated. No personally identifiable information is collected or stored.
          </p>
        </div>
        
        {/* Request Pilot CTA Banner */}
        <div className="mt-8 bg-gradient-to-r from-[#333333] to-[#444444] text-white p-6 md:p-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h3 className="text-xl md:text-2xl font-bold mb-2">Like what you see?</h3>
              <p className="text-gray-300">
                Get a personalised dashboard for {userData?.organization || 'your authority'} with real resident engagement data.
              </p>
            </div>
            <button
              onClick={() => {
                navigate('/');
                setTimeout(() => {
                  document.getElementById('pilot-form')?.scrollIntoView({ behavior: 'smooth' });
                }, 100);
              }}
              className="flex items-center justify-center gap-2 px-6 py-3 bg-[#00703C] hover:bg-[#005a30] transition-colors font-semibold whitespace-nowrap"
            >
              Request a Pilot
              <ArrowRight size={18} />
            </button>
          </div>
        </div>
      </main>
      
      {/* Footer */}
      <footer className="border-t border-gray-200 py-8 bg-white mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <TenuresLogo />
          <p className="text-sm text-gray-500 mt-4">
            Authority Analytics Dashboard | {new Date().getFullYear()} Ten<span className="text-[#00703C] font-semibold">UR</span>es.co.uk
          </p>
        </div>
      </footer>
    </div>
  );
}
